import React, { useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useSearchParams, useNavigate } from 'react-router-dom';

import { Menu, MenuItem } from '@mui/material';
import MoreVert from '@mui/icons-material/MoreVert';

import TributeWorkflowContainer from 'app/containers/modals/tributes/TributeWorkflowContainer';
import DialogsContext from 'app/contexts/DialogsContext';

import {
  useFavoriteListingMutation,
  LISTING_TAG,
} from 'app/api/mainApi';

import { authenticatedFunction } from 'app/helpers/userHelper';
import useInvalidateCacheTagsDispatcher from 'app/hooks/useInvalidateCacheTagsDispatcher';

const ListingViewerDetailKebabMenu = ({ listing, closeDrawer, currentUser }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const { openDialog } = useContext(DialogsContext);
  const [favoriteListing, { isSuccess: favoriteSuccess }] = useFavoriteListingMutation();
  const whereWeAt = window.location.pathname + window.location.search;
  const [searchParams] = useSearchParams();
  const sendTribute = searchParams.get('send_tribute');
  const favorite = searchParams.get('favorite');
  const { dispatchInvalidateCacheTagsEvent } = useInvalidateCacheTagsDispatcher();
  const navigate = useNavigate();

  const onClose = () => {
    setAnchorEl(null);
  };

  const onClick = (evt) => {
    evt.preventDefault();
    setAnchorEl(evt.currentTarget);
  };

  const onChatClick = () => {
    const chatUrl = `/chat/${listing.advisorSlug}`;
    const action = () => { window.location = chatUrl; };
    authenticatedFunction({ currentUser, action, returnUrl: chatUrl })();
  };

  const openTributeWorkflow = () => {
    setAnchorEl(null);
    closeDrawer();

    openDialog({
      component: TributeWorkflowContainer,
      props: { advisorLogin: listing.login },
    });
  };

  const onSendTributeClick = () => {
    authenticatedFunction({
      currentUser,
      action: openTributeWorkflow,
      returnUrl: `${whereWeAt}&send_tribute=true`,
    })();
  };

  const onSendMailClick = () => {
    const mailUrl = `/messages/compose?message[recipients_list]=${listing.login}&return_to=${whereWeAt}`;
    const action = () => { window.location = mailUrl; };

    authenticatedFunction({ currentUser, action, returnUrl: mailUrl })();
  };

  const oneOfFavoritesClick = () => {
    const action = () => {
      window.location = '/account/favorite';
    };

    authenticatedFunction({ currentUser, action, returnUrl: whereWeAt })();
  };

  const onAddToFavoritesClick = async () => {
    const action = async () => {
      await favoriteListing({ listing: listing.id });
      onClose();
    };

    authenticatedFunction({
      currentUser,
      action,
      returnUrl: `${whereWeAt}&favorite=true`,
    })();
  };

  useEffect(() => {
    if (favoriteSuccess) {
      dispatchInvalidateCacheTagsEvent([{ type: LISTING_TAG, id: listing.id }]);
    }
  }, [favoriteSuccess]);

  // listen for send_tribute=true in the URL,
  // indicating that we want to close the drawer
  // and open the tribute workflow
  useEffect(() => {
    if (sendTribute === 'true') {
      // we remove the listing_id so the drawer closes itself
      searchParams.delete('listing_id');
      searchParams.delete('send_tribute');

      navigate(
        `${window.location.pathname}?${searchParams.toString()}`,
        { replace: true },
      );

      openTributeWorkflow();
    }
  }, [sendTribute]);

  // listen for favorite=true in the URL,
  // indicating that we want to add to favorites
  // after the user has just been redirected to sign in
  useEffect(() => {
    if (favorite === 'true') {
      // keeping the listing_id so the drawer remains open
      searchParams.delete('favorite');

      navigate(
        `${window.location.pathname}?${searchParams.toString()}`,
        { replace: true },
      );

      onAddToFavoritesClick();
    }
  }, [favorite]);

  return (
    <>
      <a
        onClick={onClick}
        role="button"
        tabIndex={0}
        style={{ cursor: 'pointer' }}
      >
        <MoreVert sx={{ color: 'var(--color_gray_nine)', mt: 1, display: 'inline-block' }} />
      </a>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={onClose}
        data-testid="menu-popover"
      >
        {listing.show_chat_link && (
          <MenuItem onClick={onChatClick}>Chat</MenuItem>
        )}
        <MenuItem onClick={onSendTributeClick}>Send Tribute</MenuItem>
        <MenuItem onClick={onSendMailClick}>Send Mail</MenuItem>
        {listing.isFavoriteForViewer && (
          <MenuItem onClick={oneOfFavoritesClick}>One of Favorites</MenuItem>
        )}
        {!listing.isFavoriteForViewer && (
          <MenuItem onClick={onAddToFavoritesClick}>Add to Favorites</MenuItem>
        )}
      </Menu>
    </>
  );
};

ListingViewerDetailKebabMenu.defaultProps = {
  currentUser: null,
};

ListingViewerDetailKebabMenu.propTypes = {
  listing: PropTypes.object.isRequired,
  closeDrawer: PropTypes.func.isRequired,
  currentUser: PropTypes.object,
};

export default ListingViewerDetailKebabMenu;
